import api from '../api'
import { ReviewFeedback } from '@interfaces/document'
import { useTemporaryStore } from '@/contexts/temporaryStore'

const skipOnboarding = async (onboarding: boolean) => {
  return api.post('/api/user/skip-onboarding', {
    onboarding,
  })
}

const sendEmail = async (type: 'email-sequence-ghost' | 'email-sequence-app', documentPublicId: string) => {
  return api
    .post('/api/user/send-email', {
      type,
      documentPublicId,
    })
    .then(res => res.data)
}

const sendReviewFeedbackToServer = async (feedbackData: ReviewFeedback) => {
  // Calculate time difference if editing start time exists
  let timeActiveInEditor = 'N/A'
  const { editingStartTime, setEditingStartTime } = useTemporaryStore.getState()

  if (editingStartTime) {
    const currentTime = Date.now()
    const timeDifferenceMs = currentTime - editingStartTime
    const minutes = Math.floor(timeDifferenceMs / (1000 * 60))
    const seconds = Math.floor((timeDifferenceMs % (1000 * 60)) / 1000)
    timeActiveInEditor = `${minutes}m ${seconds}s`

    // Reset editing start time after calculating
    setEditingStartTime(null)
  }

  const { currentDocumentTemplateData } = useTemporaryStore.getState()

  return api
    .post('/api/events/review-feedback', {
      feedbackData,
      timeActiveInEditor,
      currentDocumentTemplateData,
    })
    .then(res => res.data)
}

const changeEmailPreference = async (action: 'unsubscribe' | 'subscribe') => {
  return api
    .post('/api/user/email-preference', {
      action,
    })
    .then(res => res.data)
}

const sendMarketingEmails = async (params: { emails: string[]; subject: string; htmlContent: string }) => {
  return api
    .post('/api/user/send-marketing-emails', {
      emails: params.emails,
      subject: params.subject,
      htmlContent: params.htmlContent,
    })
    .then(res => res.data)
}

// Email Sequence Types
export interface EmailTemplateInfo {
  id: string
  name: string
  subject: string
  description: string
  delayMs: number
  delayLabel: string
}

export interface EmailSequenceInfo {
  id: string
  name: string
  description: string
  templates: EmailTemplateInfo[]
}

export interface SendSequenceResult {
  message: string
  sequenceId: string
  sequenceName: string
  templatesUsed: { id: string; name: string }[]
  recipientCount: number
  results: {
    email: string
    templateId: string
    templateName: string
    success: boolean
    scheduledAt?: string
    error?: string
    id?: string
  }[]
  successCount: number
  failureCount: number
  immediateCount: number
  scheduledCount: number
}

export interface PreviewEmailResult {
  html: string
  subject: string
  templateId: string
  templateName: string
}

// Get available email sequences
const getEmailSequences = async (): Promise<{ sequences: EmailSequenceInfo[] }> => {
  return api.get('/api/user/email-sequences').then(res => res.data)
}

// Preview a specific email template
const previewSequenceEmail = async (params: {
  sequenceId: string
  templateId: string
  firstName?: string
}): Promise<PreviewEmailResult> => {
  return api
    .post('/api/user/preview-sequence-email', {
      sequenceId: params.sequenceId,
      templateId: params.templateId,
      firstName: params.firstName,
    })
    .then(res => res.data)
}

// Send email sequence to recipients
const sendSequenceEmails = async (params: {
  emails: string[]
  sequenceId: string
  templateIds?: string[]
}): Promise<SendSequenceResult> => {
  return api
    .post('/api/user/send-sequence-emails', {
      emails: params.emails,
      sequenceId: params.sequenceId,
      templateIds: params.templateIds,
    })
    .then(res => res.data)
}

export const user = {
  skipOnboarding,
  sendEmail,
  sendReviewFeedbackToServer,
  changeEmailPreference,
  sendMarketingEmails,
  getEmailSequences,
  previewSequenceEmail,
  sendSequenceEmails,
}
